// oxlint-disable unicorn/prefer-add-event-listener -- IndexedDB request/transaction handlers are single-owner promises.
/**
 * Public API for the local message cache (L1 memory + L2 IndexedDB), a 3-tier
 * stale-while-revalidate store ported from v1. Callers gate on `cacheOn()`
 * (app.svelte.ts → decideCacheEnabled), which is true on EVERY surface — desktop,
 * iOS, Android and plain web — unless the localStorage kill switch turns it off.
 * It delivers an instant cold render with no skeleton flash.
 *
 * Everything here is best-effort and fire-and-forget: a miss or any error must
 * degrade to the existing network fetch path. No call here ever throws into the
 * caller.
 *
 * Caveat #28: the L1 Map lives in the plain `.ts` `memCacheStore.ts` singleton.
 * Caveat #29: sync/read cursors are stamped by callers ONLY on fetch success.
 */

import { closeDB, getDB, isAvailable, STORES, type CachedMessage } from "./idb.js";
import { getCachedUserId, setCachedUserId, setMeta } from "./meta.js";
import { reportUiFailure } from "../report-failure.js";
import {
  evictIfOverCap,
  evictLegacyScopes,
  putMany,
  putOne,
  swapId,
  markDeleted,
  reconcileScope,
  dropScope,
} from "./messages.js";
import { memClear, memDrop, memUpdate } from "./memCache.js";
import {
  forgetScopeSynced,
  invalidateSyncedScopes,
  markScopeOlderHistory,
  markScopeSynced,
} from "./delta.js";
import { evictLegacySnapshots } from "./workspace-snapshot.js";
import { evictLegacyPages, evictPagesOverCap, patchLedgerMessage } from "./pages.js";
import { closeReadGate, openReadGate } from "./read-gate.js";
import { toCached } from "./transform.js";
import type { Message, ReactionChange } from "../core/types.js";
import type { ViewerIdentity } from "../session-scope.js";
import { applyReactionChange } from "../components/message/reactions/reactions.js";

// ── Re-exports (single import namespace) ─────────────────────────────────────

export {
  channelScope,
  dmScope,
  parseScope,
  messageBelongsToChannel,
  messageBelongsToDm,
  type ScopeKind,
} from "./scope.js";
export {
  memGet,
  memUpdate,
  memDrop,
  memSetCard,
  memSetWork,
  readCache,
  type MemCacheEntry,
} from "./memCache.js";
export { getRecent, getThreadReplies } from "./messages.js";
export { getMeta, setMeta, touchScope } from "./meta.js";
export { isAvailable, type CachedMessage, type CachedPage } from "./idb.js";
// Whole-page cache (CYBORG-987): agent timelines, session work, and the cybo-DM
// ledger window — the shapes the `messages` store cannot hold. See ./pages.ts.
export {
  agentTimelineKey,
  sessionWorkKey,
  cyboDmLedgerKey,
  getPage,
  putPage,
  deletePage,
  patchLedgerMessage,
} from "./pages.js";
export { toCached, fromCached } from "./transform.js";
// Delta open (CYBORG-988): decide whether an open may ask only for what is NEW
// instead of refetching the tail. Pure decision + the session-lived scope marks
// that prove the cached window is complete. See ./delta.ts.
export {
  planOpen,
  isDeltaGap,
  deltaHasMore,
  fetchPlannedPage,
  scopeSyncedAt,
  scopeOlderHistory,
  suspendSyncedScopes,
  syncedScopesSuspended,
  resumeSyncedScopes,
  scopesTouchedByChanges,
  invalidateSyncedScopes,
  noteSeqHole,
  settleSeqHoles,
  type OpenFetchPlan,
} from "./delta.js";
// Persistent send outbox (#502). NOT iOS-gated — an undelivered send must
// survive reload on every surface. See ./outbox.ts.
export {
  putOutbox,
  deleteOutbox,
  getOutboxForUser,
  clearOutbox,
  recordToCommand,
  recordToOptimisticMessage,
  localIdFor,
  isOutboxRecordStale,
  type OutboxRecord,
  type OutboxCommand,
} from "./outbox.js";
export {
  putAgentPromptOutbox,
  deleteAgentPromptOutbox,
  getAgentPromptOutbox,
  stampAgentPromptAttempt,
  updateAgentPromptOutboxText,
  markAgentPromptOutboxQueued,
  type AgentPromptOutboxRecord,
} from "./agent-prompt-outbox.js";

// ── Lifecycle ────────────────────────────────────────────────────────────────

let initPromise: Promise<void> | null = null;
let initForOwner: string | null = null;

/** The cache's owner key — see cacheInit. Lowercased so case can't split it. */
function ownerKey(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Open the DB and reconcile against the current session user. Wipes the cache
 * if a DIFFERENT human previously owned it (shared-device scenario) so user B
 * never sees user A's messages. Idempotent per owner within a session.
 *
 * This is the precondition for caching on plain web, and it is platform-blind by
 * construction: nothing here reads the shell, only `isAvailable()` (indexedDB
 * present). connectToServer calls it behind `cacheOn()`, so turning web on is
 * exactly what arms the wipe there — a shared browser where B logs in after A
 * clears L1+L2 before B reads a single scope. cache-owner.test.ts drives that
 * plain-web case end to end.
 *
 * Keyed on the EMAIL, not the opaque user id. One human does NOT have one id
 * here: the cloud relay answers `cyborg:auth` with the PG `users.id`
 * (`randomUUID` at signup) while a daemon-direct / self-hosted login answers
 * with `canonicalUserId(email)` (a uuidv5 of the address). Same person, same
 * device, two ids — so an id-keyed compare read every cloud↔self-host switch as
 * "a different user" and wiped the whole cache on cold launch. Email is the
 * stable human identity and is already what browser-controller.ts,
 * terminal-controller.ts, composio-deps.ts,
 * approval-card-daemon.ts and schedule-authz.ts match on for exactly this
 * reason; this was the last raw exact-id compare left.
 *
 * (The send outbox deliberately stays id-keyed: its rows are undelivered sends
 * for ONE endpoint, and they must not follow you to a different relay.)
 *
 * Upgrade note: a cache written before this change stored an id under the same
 * appMeta key, so the first launch after upgrading wipes once and then stays
 * stable. That is a cold-launch repaint, not data loss — cacheClear() does not
 * touch the outbox store.
 */
export function cacheInit(email: string): Promise<void> {
  if (!isAvailable() || !email) return Promise.resolve();
  const owner = ownerKey(email);
  if (!owner) return Promise.resolve();
  if (initPromise && initForOwner === owner) return initPromise;
  const previous = initPromise;
  initForOwner = owner;
  // Opened in this tick, so a read issued right after sign-in already waits (CYBO-1846 B1).
  const ready = (previous ?? Promise.resolve()).then(() => claimForOwner(owner));
  openReadGate(ready);
  initPromise = ready.then(sweepAfterClaim);
  return initPromise;
}

// Housekeeping once the owner holds the cache; the gate has already decided what may be read.
async function sweepAfterClaim(ok: boolean): Promise<void> {
  if (!ok) return;
  try {
    await evictLegacyScopes();
    await evictLegacyPages();
    await evictLegacySnapshots();
    await evictIfOverCap();
    await evictPagesOverCap();
  } catch {
    // intentional: best-effort eviction; a row it misses is swept on the next sign-in.
  }
}

// Make this device's cache `owner`'s: wipe another's (or an unrecorded owner's) rows, then
// prove the owner was stored. false = every read this session is a miss (fail closed).
async function claimForOwner(owner: string): Promise<boolean> {
  try {
    if ((await getCachedUserId()) !== owner && !(await wipeStores())) {
      return readGateClosed("wipe_failed");
    }
    await setCachedUserId(owner);
    if ((await getCachedUserId()) !== owner) return readGateClosed("owner_mismatch");
    return true;
  } catch (err) {
    reportUiFailure("cache.read_gate", err, {}, { failureKind: "fault", code: "init_failed" });
    return false;
  }
}

// Reason code only: the owner key is an address and never reaches a log.
function readGateClosed(code: string): false {
  const cause = new Error(`cache read gate closed: ${code}`);
  reportUiFailure("cache.read_gate", cause, {}, { failureKind: "fault", code });
  return false;
}

/**
 * Wipe L1 + L2 (sign-out / user switch).
 *
 * Derived from the DB's OWN store list (CYBORG-987): a store added later is wiped because it
 * EXISTS. The channel/DM OUTBOX is the one exclusion: its rows are undelivered sends for ONE
 * endpoint, keyed by user id. The agent prompt outbox is wiped (it can hold transcript text).
 */
export async function cacheClear(): Promise<void> {
  closeReadGate();
  // Reset before any await: a same-person sign-in during the wipe must start a fresh init
  // (queued behind the wipe), not be handed the closed one, which would leave the cache off.
  const wiping = wipeStores().then(() => undefined);
  initPromise = wiping;
  initForOwner = null;
  await wiping;
}

/** The wipe itself; false when it failed and the previous account's rows may remain. */
async function wipeStores(): Promise<boolean> {
  memClear();
  // CYBORG-988: the delta marks describe the windows a wipe destroys, so they go with them.
  invalidateSyncedScopes();
  if (!isAvailable()) return true;
  try {
    const dbp = getDB();
    if (!dbp) return true; // no database: nothing persisted to leak
    const db = await dbp;
    const names = wipedStores(db.objectStoreNames);
    if (names.length === 0) return true;
    const tx = db.transaction(names, "readwrite");
    for (const name of names) tx.objectStore(name).clear();
    return await new Promise<boolean>((resolve) => {
      tx.oncomplete = () => resolve(true);
      // A failed wipe leaves the previous account's messages on this device.
      tx.onerror = () => {
        reportUiFailure("cache.clear", tx.error ?? new Error("cache wipe transaction failed"));
        resolve(false);
      };
      tx.onabort = () => {
        reportUiFailure("cache.clear", tx.error ?? new Error("cache wipe transaction aborted"));
        resolve(false);
      };
    });
  } catch (err) {
    reportUiFailure("cache.clear", err);
    return false;
  }
}

/** Hard reset — close + delete the DB (corruption recovery; not normally used). */
export async function cacheDestroy(): Promise<void> {
  closeReadGate();
  memClear();
  if (!isAvailable()) return;
  await closeDB();
  try {
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase("cyborg7-cache");
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    });
  } catch {
    /* */
  }
  initPromise = null;
  initForOwner = null;
}

// ── High-level writeback helpers (what app.svelte.ts calls) ──────────────────

/**
 * Persist the authoritative window for a scope after a SUCCESSFUL network
 * fetch: writes the rows to L2, reconciles phantoms inside the response window,
 * refreshes L1, and stamps the sync + (optional) read cursors. Caveat #29 — the
 * caller only calls this on success, never on a miss/error.
 *
 * `messages` is the full in-memory list the UI is showing (top-level only is
 * fine — replies are written separately by the thread path). Fire-and-forget.
 *
 * `fromFullRead` says whether the page behind this write was a FULL read (the
 * tail / around-window read) or a DELTA. Required, not defaulted: a caller that
 * forgets it must fail to compile rather than silently re-arm the backstop.
 */
export async function writeScopeMessages(
  scopeKey: string,
  memKey: string,
  messages: Message[],
  lastReadAt: number | null,
  fromFullRead: boolean,
  hasOlder?: boolean,
): Promise<void> {
  if (!scopeKey) return; // signed out: no viewer, no scope (CYBO-1846)
  // Refresh L1 synchronously-ish (it's a sync Map op wrapped in async). No `local-` row, as in L2:
  // its status here would go stale, and its kept copy (reapplyOutboxBubbles) is the one that returns.
  memUpdate(
    memKey,
    messages.filter((m) => !m.id.startsWith("local-")),
    lastReadAt,
  );
  // The older-edge answer travels with the window it describes (CYBORG-1194).
  if (hasOlder !== undefined) markScopeOlderHistory(scopeKey, hasOlder);
  // CYBORG-988: this is the ONE fetch-success seam, so it is where a scope earns
  // its delta mark. Stamped next to the L1 refresh, not inside the try below: the
  // delta splices onto the window L1 just took, and that assignment cannot fail.
  // Caveat #29 holds — the caller only reaches here on a successful fetch.
  //
  // CYBORG-992: only a FULL read stamps it. The mark's age is what
  // SYNCED_MARK_MAX_AGE_MS bounds, and re-stamping on a delta made that age reset
  // on every open — so the backstop could never fire for a conversation the user
  // actually uses. A delta writeback leaves the mark exactly where the last full
  // read put it; a delta that lands after a wipe (disconnect mid-flight) leaves
  // the scope UNMARKED, so its next open reads the tail. Both are the safe way.
  if (fromFullRead) markScopeSynced(scopeKey, Date.now());
  try {
    const topLevel = messages.filter((m) => !m.parentId && !m.id.startsWith("local-"));
    const rows: CachedMessage[] = topLevel.map((m) => toCached(scopeKey, m));
    if (rows.length === 0) {
      await setMeta(scopeKey, { lastAccessedAt: Date.now(), lastReadAt: lastReadAt ?? undefined });
      return;
    }
    await putMany(rows);
    const ids = new Set(rows.map((r) => r.id));
    let oldest = rows[0].createdAt;
    let newest = rows[0].createdAt;
    let maxSeq = 0;
    for (const r of rows) {
      if (r.createdAt < oldest) oldest = r.createdAt;
      if (r.createdAt > newest) newest = r.createdAt;
      if (r.seq > maxSeq) maxSeq = r.seq;
    }
    await reconcileScope(scopeKey, ids, oldest, newest);
    await setMeta(scopeKey, {
      lastSyncedSeq: maxSeq,
      lastReadAt: lastReadAt ?? undefined,
      lastAccessedAt: Date.now(),
    });
  } catch {
    /* best-effort */
  }
}

/** Upsert a single live message (channel_message / dm). Fire-and-forget. */
export async function cacheUpsertMessage(scopeKey: string, msg: Message): Promise<void> {
  try {
    if (!scopeKey || msg.id.startsWith("local-")) return; // no viewer, or an optimistic row
    await putOne(toCached(scopeKey, msg));
  } catch {
    /* */
  }
}

/** Swap an optimistic local row for its real-id echo. Fire-and-forget. */
export async function cacheSwapId(scopeKey: string, localId: string, real: Message): Promise<void> {
  if (!scopeKey) return;
  try {
    await swapId(localId, toCached(scopeKey, real));
  } catch {
    /* */
  }
}

/** The cached row after an edit. A system line changing state (`source`) is not an author edit: no "edited" stamp. */
export function withEdit<T extends object>(m: T, text: string, source?: string): T {
  return { ...m, text, ...(source ? { source } : { updatedAt: Date.now() }) };
}

/**
 * Edit a message's text in place. Fire-and-forget.
 *
 * Both stores, not just `messages` (CYBORG-992): the cybo-DM ledger window in the
 * `pages` store holds the same rows by id, and it is a UNION that only ever grows, so
 * an edit that never reached it kept the pre-edit text alive on every re-open.
 */
export async function cacheEditMessage(
  messageId: string,
  text: string,
  source?: string,
): Promise<void> {
  try {
    await patchById(messageId, (m) => withEdit(m, text, source));
  } catch {
    /* */
  }
  await patchLedgerMessage(messageId, text);
}

/** Tombstone a message (+ cascade replies) in BOTH stores. Fire-and-forget. */
export async function cacheDeleteMessage(messageId: string): Promise<void> {
  try {
    await markDeleted([messageId]);
  } catch {
    /* */
  }
  await patchLedgerMessage(messageId, null);
}

/** Patch a message's pin state. Fire-and-forget. */
export async function cachePinMessage(
  messageId: string,
  pinnedAt: number | null,
  pinnedBy: string | null,
): Promise<void> {
  try {
    await patchById(messageId, (m) => ({ ...m, pinnedAt, pinnedBy }));
  } catch {
    /* */
  }
}

/** Patch unfurls onto a message. Fire-and-forget. */
export async function cacheUnfurlMessage(messageId: string, unfurls: unknown[]): Promise<void> {
  try {
    await patchById(messageId, (m) => ({ ...m, unfurls }));
  } catch {
    /* */
  }
}

/** Patch a re-rendered card onto the cached row. Fire-and-forget. */
export async function cacheCardMessage(messageId: string, card: unknown): Promise<void> {
  try {
    await patchById(messageId, (m) => ({ ...m, card: card ?? null })); // null: the cached row's own "no card"
  } catch {
    // intentional: the cache is best-effort, the open conversation is already patched
  }
}

/** Patch the turn-end summary onto the cached rows. Fire-and-forget. */
export async function cacheWorkMessages(
  messageIds: readonly string[],
  work: Message["workSummary"],
): Promise<void> {
  for (const id of messageIds) {
    try {
      await patchById(id, (m) => ({ ...m, workSummary: work }));
    } catch {
      // intentional: the cache is best-effort, the open conversation is already patched
    }
  }
}

/** Apply a live reaction change to the cached row — the same reducer the panes use. */
export async function cacheApplyReaction(
  change: ReactionChange,
  viewer: ViewerIdentity,
): Promise<void> {
  try {
    await patchById(change.messageId, (m) => ({
      ...m,
      reactions: applyReactionChange(m.reactions, change, viewer),
      updatedAt: Date.now(),
    }));
  } catch {
    /* */
  }
}

/** Drop a scope entirely (channel deleted / left). Fire-and-forget. */
export async function cacheDropScope(memKey: string, scopeKey: string): Promise<void> {
  memDrop(memKey);
  forgetScopeSynced(scopeKey);
  try {
    await dropScope(scopeKey);
  } catch {
    /* */
  }
}

/**
 * Which stores a wipe clears: every store in the DB except the send outbox. Pure +
 * exported so the "a new store is wiped by default" invariant is a unit test rather
 * than a promise (see cache-owner.test.ts).
 */
export function wipedStores(storeNames: Iterable<string>): string[] {
  return [...storeNames].filter((n) => n !== STORES.OUTBOX);
}

// Read-modify-write a single cached row by id (used by the live-patch helpers).
async function patchById(id: string, fn: (m: CachedMessage) => CachedMessage): Promise<void> {
  const dbp = getDB();
  if (!dbp) return;
  const db = await dbp;
  const tx = db.transaction("messages", "readwrite");
  const store = tx.objectStore("messages");
  const existing = await new Promise<CachedMessage | undefined>((resolve, reject) => {
    const r = store.get(id);
    r.onsuccess = () => resolve(r.result as CachedMessage | undefined);
    r.onerror = () => reject(r.error);
  });
  if (existing) store.put(fn(existing));
  await new Promise<void>((resolve) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
}
